/**
 * Palett + MapLibre-stil (root-nivå).
 *
 * Uttrykk: dempet, grafisk, premium. Arkitektonisk modell /
 * redaksjonell informasjonsgrafikk – ikke Google Maps.
 *
 * All basiskart-informasjon holdes visuelt underordnet rutene
 * vi legger på senere.
 */

import { buildBaseLayers } from './layers.js';
import { TERRAIN_SOURCE_ID, terrainSource } from './terrain.js';

/**
 * Sentral fargepalett. Endre farger her – lagene refererer til denne.
 */
export const PALETTE = {
  background: '#cfc7b6', // mørkere varm beige/grå
  water: '#8ea3ad', // dempet blågrå
  waterDeep: '#7f96a1',
  forest: '#5f7a55', // mørkegrønn skog
  grass: '#8ea07f', // dypere grønn-grå (park/gress)
  landuse: '#c8c1af', // mørkere varm landuse
  pier: '#c4a878', // lysere varm brun – ekte brygge/kaikonstruksjon over vann
  plaza: '#d6cdba', // nøytralt gangdekke/torg (Rådhusplassen o.l.)
  building: '#c3b8a2', // varm grå (bunn)
  buildingTop: '#e0d4ba', // varmere tak – som morgensol treffer

  // Veihierarki – fra svakt til tydelig
  roadPath: '#c2b8a5', // stier – svakt
  roadService: '#cabfad',
  roadResidential: '#ded7c8',
  roadResidentialCasing: '#bdb4a1',
  roadTertiary: '#dcd3c2',
  roadSecondary: '#d3c9b4',
  roadSecondaryCasing: '#b3a891',
  roadPrimary: '#d0c4ac',
  roadPrimaryCasing: '#a99c84',
  roadMajor: '#a2957d', // motorway/trunk fyll – tydeligere mørk grå
  roadMajorCasing: '#897c65',

  rail: '#a39b8c', // dempet grå

  labelText: '#3a362f', // mørk grå
  labelHalo: '#d9d2c2',
  labelWater: '#4f5e66',
};

// OpenFreeMap / OpenMapTiles vector tiles.
// Standard OpenMapTiles-skjema. Source-layer-navn er verifisert mot
// https://tiles.openfreemap.org/planet (vector_layers):
//   water, waterway, landcover, landuse, park, building,
//   transportation, transportation_name, place, ...
export const TILE_URL = 'https://tiles.openfreemap.org/planet';

// Glyphs (fonts) for tekst-labels.
export const GLYPHS = 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf';

/**
 * Morgenlys.
 *
 * Lav, varm sol fra øst/sørøst. Treffer fill-extrusion-bygningene:
 * gir en varm, opplyst side og myke skygger på bygningsflatene.
 *
 *   anchor:   'map'  – lyset er festet til kartet (verden), ikke skjermen,
 *                      så solretningen holder seg når du roterer.
 *   position: [radial, azimuth, polar]
 *             radial  – avstand (1.15 = litt hevet)
 *             azimuth – kompassretning i grader (90 = øst → morgensol)
 *             polar   – høyde over horisonten (80 = lavtstående sol)
 */
export const MORNING_LIGHT = {
  anchor: 'map',
  position: [1.15, 90, 80],
  color: '#ffe6c2', // varm, gyllen morgentone
  intensity: 0.45,
};

/**
 * Atmosfærisk dis + himmel (MapLibre `sky`).
 *
 * MapLibre har ikke Mapbox' `fog` – dis lages i stedet via `sky` med
 * fog-* egenskapene, som legger en varm dis lavt mot horisonten og
 * blender den mot bakken ved høy pitch. Gir morgenstemning.
 *
 *   sky-color        – farge høyt oppe
 *   horizon-color    – farge ved horisonten
 *   fog-color        – disens farge (varm morgentåke)
 *   *-blend          – hvor mykt sonene går over i hverandre
 */
export const MORNING_SKY = {
  'sky-color': '#cfe0e8', // svakt kjølig morgenhimmel
  'sky-horizon-blend': 0.6,
  'horizon-color': '#f0e6d6', // varm horisont
  'horizon-fog-blend': 0.6,
  'fog-color': '#efe4d2', // varm, lys dis
  'fog-ground-blend': 0.6, // hvor langt disen trekker ned mot bakken
  'atmosphere-blend': [
    // Fade inn dis når vi tilter kameraet; borte i rett nedsyn.
    'interpolate', ['linear'], ['zoom'],
    9, 0.6,
    16, 0.3,
  ],
};

/**
 * Bygger et komplett MapLibre-stilobjekt.
 */
export function buildStyle() {
  return {
    version: 8,
    name: 'ruterracet-editorial',
    glyphs: GLYPHS,
    light: MORNING_LIGHT,
    sky: MORNING_SKY,
    sources: {
      openmaptiles: {
        type: 'vector',
        url: TILE_URL,
      },
      // Høydemodell fra Kartverket. Kilden legges alltid inn; selve
      // terrenget slås på i main.js etter at stilen er lastet.
      [TERRAIN_SOURCE_ID]: terrainSource(import.meta.env.BASE_URL),
    },
    layers: [
      {
        id: 'background',
        type: 'background',
        paint: {
          'background-color': PALETTE.background,
        },
      },
      ...buildBaseLayers(),
    ],
  };
}
